import React, { useEffect } from 'react';
import Modal from "../../../components/common/Modal";
import { Button } from '../../../components/common/Button';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { useInterview } from '../contexts/InterviewContext';
import ModalPortal from '../../../components/common/ModalPortal';

interface RestartConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * RestartConfirmationModal - Modal for confirming interview restart
 * 
 * This component is responsible for:
 * 1. Displaying a confirmation dialog when restarting an interview
 * 2. Providing confirm/cancel actions
 * 3. Ensuring media tracks are preserved during modal display
 */
const RestartConfirmationModal: React.FC<RestartConfirmationModalProps> = ({ 
  isOpen, 
  onClose,
  onConfirm
}) => {
  // Prevent modal from affecting camera stream
  useEffect(() => {
    if (isOpen) {
      // When modal opens, ensure all media tracks maintain their state
      const maintainMediaTracks = () => {
        const videoEl = document.querySelector('video');
        if (videoEl && videoEl.srcObject instanceof MediaStream) {
          const mediaStream = videoEl.srcObject;
          mediaStream.getTracks().forEach(track => {
            // Don't change the state, just ensure track properties are preserved
            console.log(`Preserving ${track.kind} track state: ${track.enabled ? 'enabled' : 'disabled'}`);
          });
        }
      };
      
      maintainMediaTracks();
    }
  }, [isOpen]);
  
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  if (!isOpen) return null;
  
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in z-[100]">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full border border-border-light dark:border-border-dark animate-fade-in-up">
          <div className="p-5 border-b border-border-light dark:border-border-dark">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
                <RefreshCcw className="w-5 h-5 text-primary-light dark:text-primary-dark" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                Restart Interview
              </h3>
            </div>
          </div>
          <div className="p-5">
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-4">
              Are you sure you want to restart this interview? The new interview will start from the beginning & more interview minutes will be used for the new interview.
            </p>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={onClose}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleConfirm}
                className="w-full sm:w-auto"
              >
                Yes, Restart Interview
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default RestartConfirmationModal; 